import * as Sentry from '@sentry/react';
import Axios, {
  AxiosError,
  type InternalAxiosRequestConfig,
  isCancel,
} from 'axios';

import { paths } from '@/config/paths';
import { type AuthToken, tokenSchema } from '@/features/auth/types/token';
import { queryClient } from '@/lib/query-client';

function authRequestInterceptor(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig<unknown> {
  if (config.headers) {
    config.headers.Accept = 'application/json';
  }

  // Add auth token to request if available.
  //
  // The parse must not be allowed to throw. A request interceptor that throws
  // rejects before dispatch, so the error carries no `response` and no
  // `config`: the 401 branch below never runs (the user is never sent to
  // login) and the health-check refetch fires on every call, including on the
  // health check itself. A token in a stale shape would therefore brick the
  // app with no recovery path. Treat an unreadable token as no token instead,
  // and drop it so the normal unauthenticated flow can take over.
  const rawToken = localStorage.getItem('token');
  let token: AuthToken | null = null;

  if (rawToken) {
    try {
      token = tokenSchema.parse(JSON.parse(rawToken));
    } catch {
      localStorage.removeItem('token');
    }
  }

  if (token) {
    // Token type is returned as "bearer" but the header expects "Bearer"
    config.headers.Authorization = `Bearer ${token.accessToken}`;
  }

  config.baseURL = token
    ? import.meta.env.VITE_API_HOST
    : import.meta.env.VITE_NH1816_PROXY;

  config.withCredentials = true;
  return config;
}

export const api = Axios.create({
  baseURL: import.meta.env.VITE_NH1816_PROXY,
});

api.interceptors.request.use(authRequestInterceptor);
api.interceptors.response.use(
  (response) => {
    // In some other repositories we extract the data from the Axios object here,
    // this causes issues later since the typing isn't updated accordingly.
    return response;
  },
  (error) => {
    // A request aborted by the browser, e.g. when navigating away, is not a
    // failure. No timeout is set, so ECONNABORTED only comes from an abort.
    if (isCancel(error) || error.code === AxiosError.ECONNABORTED) {
      return Promise.reject(error);
    }

    const message = error.response?.data?.message || error.message;
    Sentry.captureException(error, {
      tags: {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status_code: error.response?.status,
      },
      extra: {
        description: message,
      },
    });

    const status = error.response?.status;

    if (
      status === 401 &&
      !window.location.href.includes(paths.auth.login.path)
    ) {
      const searchParams = new URLSearchParams(window.location.search);
      const redirectTo =
        searchParams.get('redirectTo') || window.location.pathname;
      window.location.href = paths.auth.login.getHref(redirectTo);
    }

    const isNetworkError = !error.response;
    const isServerError = status !== undefined && status >= 500;
    // An error raised before dispatch has no config. Treat it as a health
    // check so it cannot re-trigger the probe, which would then fail the same
    // way and loop.
    const isHealthCheck =
      !error.config || error.config.url?.includes('health_check');
    if ((isNetworkError || isServerError) && !isHealthCheck) {
      queryClient.refetchQueries({ queryKey: ['health-check'] });
    }

    return Promise.reject(error);
  },
);
